import React, { useEffect, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import CardHeader from '@material-ui/core/CardHeader';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import useSetQuery from 'customHooks/useQuery';
import { CardContent, useMediaQuery } from '@material-ui/core';
import { pwaSettings } from 'configs/pwaConstants';
import LAOChartCard from './LAO_Common/LAOChartCard';
import Slider from 'react-slick/lib/slider';
import { chartsHeight as height } from 'helpers/styleHelpers';
import { dark } from '../../theme/palette';

export const useStylesTimeline = makeStyles((theme) => ({
    rootClassTimeline: {
        height: "auto",
        minHeight: theme.spacing(25),
        borderRadius: theme.spacing(1),
        background: theme.palette.card.default,
    },
    titleClassTimeline: {
        marginRight: "auto",
        paddingBottom: theme.spacing(2),
        marginTop: theme.spacing(1),
        fontSize: theme.spacing(2),
        textTransform: "none",
        [theme.breakpoints.down('sm')]: {
            paddingBottom: 0,
        },
        [theme.breakpoints.down('xs')]: {
            textAlign: 'center'
        }
    },
    radioFilterGridClass: {
        textAlign: 'right',
        justifyContent: "space-between",
        [theme.breakpoints.down('xs')]: {
            textAlign: 'center'
        }
    },
    radioGroupClass: {
        justifyContent: "space-between",
    },
    sliderClass: {
        margin: `0px ${theme.spacing(2.25)}px ${theme.spacing(1)}px`,
        "& .slick-current": {
            display: "flex",
            justifyContent: "center"
        }
    }
}));

const radioFilters = {
    posoItems: {
        value: "posoItems",
        label: "po_so_items"
    },
    shipments: {
        value: "shipments",
        label: "shipments"
    },
    containers: {
        value: "containers",
        label: "containers"
    }
}

const TitleWithFilters = ({ timelineType, setTimelineType }) => {
    const classesTimeline = useStylesTimeline();
    const { t } = useTranslation();
    const [params, setParamsLao] = useSetQuery();

    const handleTimelineTypeChange = (e) => {
        setParamsLao({ key: "timelineType", value: e.target.value });
        setTimelineType(e.target.value)
    }

    return <Grid container direction="row">
        <Grid item xs={12} sm={3} md={3} lg={3} >
            <Typography className={classesTimeline.titleClassTimeline} variant="h3" component={"h3"}>{t("timeline_KPI").toUpperCase()}</Typography>
        </Grid>
        <Grid item xs={12} sm={9} md={9} lg={9} className={classesTimeline.radioFilterGridClass}>
            <FormControl data-testid="timelineRadioFilters" >
                <RadioGroup row
                    name="timelineType"
                    className={classesTimeline.radioGroupClass}
                    defaultValue={timelineType}
                    onChange={(e) => handleTimelineTypeChange(e)}
                    data-testid="timlineType"
                >
                    {Object.keys(radioFilters).map(key => {
                        return <FormControlLabel
                            key={key}
                            value={radioFilters[key]?.value}
                            control={<Radio data-testid={`radio-${key}`} />}
                            label={<Typography color="primary">{t(radioFilters[key]?.label)}</Typography>}
                        />
                    })}
                </RadioGroup>
            </FormControl>
        </Grid>
    </Grid>
}


const LaoTimeLineKpiWrapper = ({ defaultFilters }) => {
    const classesTimeline = useStylesTimeline({ height });
    const { t } = useTranslation();
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [innerWidth, setInnerWidth] = useState(() => window.innerWidth - window.innerWidth / 10 * 1.5)
    const [timelineType, setTimelineType] = useState("posoItems");

    useEffect(() => {
        const handleResize = () => setInnerWidth(() => window.innerWidth - window.innerWidth / 10 * 1.5);
        window.addEventListener('resize', handleResize);
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const subTypes = {
        exportVendors: {
            posoItems: "TIMELINE_KPI_EXT_VENDORS_PO_LINE",
            containers: "TIMELINE_KPI_EXT_VENDORS_CONTAINERS",
            shipments: "TIMELINE_KPI_EXT_VENDORS_SHIPMENTS"
        },
        interCompany: {
            posoItems: "TIMELINE_KPI_INTER_COMPANY_PO_LINE",
            containers: "TIMELINE_KPI_INTER_COMPANY_CONTAINERS",
            shipments: "TIMELINE_KPI_INTER_COMPANY_SHIPMENTS"
        },
        directShipment: {
            posoItems: "TIMELINE_KPI_DIRECT_SHIPMENT_PO_LINE",
            containers: "TIMELINE_KPI_DIRECT_SHIPMENT_CONTAINERS",
            shipments: "TIMELINE_KPI_DIRECT_SHIPMENT_SHIPMENTS"
        },
    }

    const chartHeight = isMobile ? 950 : 400;
    const charts = [
        {
            title: t("export_vendors"),
            subtype: subTypes.exportVendors[timelineType],
            chart: "process",
            colorPalette: dark?.timelineChart1,
            testId: "export-vendors-chart",
            page: "timelineKPI",
            height: chartHeight,
            minWidth: 500,
            maxWidth: 500,
        },
        {
            title: t("intercompany"),
            subtype: subTypes.interCompany[timelineType],
            chart: "process",
            colorPalette: dark?.timelineChart2,
            testId: "intercompany-chart",
            page: "timelineKPI",
            height: chartHeight,
            minWidth: 500,
            maxWidth: 500,
        },
        {
            title: t("direct_shipment"),
            subtype: subTypes.directShipment[timelineType],
            chart: "process",
            colorPalette: dark?.timelineChart3,
            testId: "direct-shipment-chart",
            page: "timelineKPI",
            height: chartHeight,
            minWidth: 500,
            maxWidth: 500,
        },
    ]


    return (
        <Card className={classesTimeline.rootClassTimeline} data-testid="lao-timeline-kpis-wrapper">
            <CardHeader title={<TitleWithFilters setTimelineType={setTimelineType} timelineType={timelineType} />} />

            {
                isMobile && <>
                    <Slider {...pwaSettings} className={classesTimeline.sliderClass}>
                        {
                            charts.map(d => {
                                return (
                                    <div key={d?.title}>
                                        <LAOChartCard {...d} maxWidth={innerWidth} minWidth={innerWidth} boxShadow="none" defaultFilters={defaultFilters} />
                                    </div>
                                )
                            })
                        }
                    </Slider>
                </>
            }
            {
                !isMobile && <>
                    <CardContent p={0}>
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                            justifyContent="center"
                        >
                            {
                                charts.map(d => {
                                    return (
                                        <Grid item p={1} key={d?.title}>
                                            <LAOChartCard {...d} maxWidth={innerWidth} minWidth={innerWidth} boxShadow="none" defaultFilters={defaultFilters} />
                                        </Grid>
                                    )
                                })
                            }
                        </Grid>
                    </CardContent>
                </>
            }
        </Card>
    );
}

export default LaoTimeLineKpiWrapper